import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Search, Download } from 'lucide-react';
import MainService from '../../services/MainService';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';
import HighlightList from '../ui/HighlightList';
import DataColumnMenu from '../ui/DataColumnMenu';
import { columnHasActiveFilter, groupRows } from '../ui/columnMenuUtils';
import { useGroupCollapse, GroupByBanner, GroupHeaderLabel } from '../ui/groupCollapse';
import { flattenFiltersForApi } from '../../utils/listFilters';
import { PAGE_SIZE_OPTIONS, buildPageItems } from '../../utils/listPagination';
import { exportGroupedExcel } from '../../utils/excelExport';

const todayInput = () => new Date().toISOString().split('T')[0];

const parseList = (response) => {
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response)) return response;
  return [];
};
const parseTotal = (response, fallback = 0) => Number(response?.Total ?? response?.total ?? fallback) || 0;
const dateParts = (value) => {
  const date = value ? new Date(value) : new Date();
  return { FromDay: date.getDate(), FromMonth: date.getMonth() + 1, FromYear: date.getFullYear() };
};
const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-GB');
};
const formatAmount = (value) => Number(value || 0).toFixed(2);
const statusOptions = ['Processing', 'Payment Done', 'Payment Return'];

const SERVER_FILTER_FIELDS = new Set([
  'clientname',
  'totalorders',
  'refno',
  'paymentrefno',
  'paymentdate',
  'employeename',
  'status',
  'clientmoney',
]);

const buildClientRequest = (keyword) => ({
  filter: {
    filters: [{ field: 'Name', operator: 'contains', value: keyword }],
    logic: 'and',
  },
});

const buildColumns = (isPayment, amountLabel) => {
  const cols = [
    { field: 'clientname', title: 'Client', widthClass: 'w-[14%]', hideable: true },
    { field: 'totalorders', title: 'Total Orders', widthClass: 'w-[8%]', hideable: true },
    { field: 'refno', title: 'Ref No', widthClass: 'w-[10%]', hideable: true },
    {
      field: 'paymentrefno',
      title: isPayment ? 'Payment Ref No' : 'Return Ref No',
      widthClass: 'w-[12%]',
      hideable: true,
    },
  ];
  if (isPayment) {
    cols.push({ field: 'paymentdate', title: 'Payment Date', widthClass: 'w-[10%]', hideable: true });
  }
  cols.push(
    { field: 'employeename', title: 'Employee', widthClass: 'w-[12%]', hideable: true },
    { field: 'status', title: 'Status', widthClass: 'w-[10%]', hideable: true }
  );
  if (isPayment) {
    cols.push({ field: 'clientmoney', title: amountLabel || 'Client Money', widthClass: 'w-[10%]', hideable: true });
  } else {
    cols.push({ field: 'parentstatus', title: 'Client Return Status', widthClass: 'w-[12%]', hideable: true });
  }
  return cols;
};

const defaultVisibility = (columns) =>
  columns.reduce((acc, col) => {
    acc[col.field] = true;
    return acc;
  }, {});

const getCellValue = (row, field, parentKey, amountKey) => {
  const parent = row[parentKey] || {};
  switch (field) {
    case 'clientname':
      return row.Client?.Name || '-';
    case 'totalorders':
      return row.TotalOrders ?? parent.TotalOrders ?? 0;
    case 'refno':
      return row.RefNo || '-';
    case 'paymentrefno':
      return parent.RefNo || '-';
    case 'paymentdate':
      return formatDate(parent.Date);
    case 'employeename':
      return parent.EmployeeName || '-';
    case 'status':
      return row.Status || '-';
    case 'clientmoney':
      return formatAmount(parent[amountKey] ?? row[amountKey]);
    case 'parentstatus':
      return parent.Status || '-';
    default:
      return row[field];
  }
};

const filtersForApi = (filtersByField) => {
  const serverOnly = {};
  Object.entries(filtersByField || {}).forEach(([field, value]) => {
    if (SERVER_FILTER_FIELDS.has(field)) {
      serverOnly[field] = value;
    }
  });
  return flattenFiltersForApi(serverOnly);
};

const ClientFollowupPage = ({ title, endpoint, parentKey, amountLabel, amountKey }) => {
  const isPayment = parentKey === 'ClientPayment';
  const columns = useMemo(() => buildColumns(isPayment, amountLabel), [isPayment, amountLabel]);

  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState({ Date: todayInput(), Keyword: '', EmployeeId: '', Status: '', ClientIds: [] });
  const [appliedFilter, setAppliedFilter] = useState(filter);
  const [selectedClients, setSelectedClients] = useState([]);
  const [clientKeyword, setClientKeyword] = useState('');
  const [clientOptions, setClientOptions] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filtersByField, setFiltersByField] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(() => defaultVisibility(columns));
  const [groupField, setGroupField] = useState(null);
  const loadSeqRef = useRef(0);
  const pageSizeBeforeGroupRef = useRef(null);

  useEffect(() => {
    setColumnVisibility(defaultVisibility(columns));
  }, [columns]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadEmployees = useCallback(async () => {
    try {
      setEmployees(parseList(await MainService.EmployeesInDropDown()));
    } catch (err) {
      console.error('Load employees error:', err);
    }
  }, []);

  const fetchRows = useCallback(async ({
    page: pageArg,
    pageSize: pageSizeArg,
    applied: appliedArg,
    sortField: sortFieldArg,
    sortDirection: sortDirectionArg,
    filters: filtersArg,
  } = {}) => {
    const pageNum = pageArg ?? page;
    const size = pageSizeArg !== undefined ? pageSizeArg : pageSize;
    const applied = appliedArg !== undefined ? appliedArg : appliedFilter;
    const sField = sortFieldArg !== undefined ? sortFieldArg : sortField;
    const sDir = sortDirectionArg !== undefined ? sortDirectionArg : sortDirection;
    const filters = filtersArg !== undefined ? filtersArg : filtersByField;
    const seq = ++loadSeqRef.current;
    const isAll = size === 'all';

    try {
      setLoading(true);
      setError('');
      const body = {
        ...dateParts(applied.Date),
        Keyword: applied.Keyword?.trim() || '',
        EmployeeId: Number(applied.EmployeeId) || 0,
        Status: applied.Status || '',
        ClientIds: applied.ClientIds || [],
        Skip: isAll ? 0 : (pageNum - 1) * Number(size),
        Take: isAll ? 0 : Number(size),
        SortField: sField || null,
        SortDir: sDir || 'asc',
        Filters: filtersForApi(filters),
      };

      const response = await MainService[endpoint](body);
      if (seq !== loadSeqRef.current) return;
      const list = parseList(response);
      setRows(list);
      setTotalCount(parseTotal(response, list.length));
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error(`${endpoint} error:`, err);
      setRows([]);
      setTotalCount(0);
      setError('Network Error');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [page, pageSize, appliedFilter, sortField, sortDirection, filtersByField, endpoint]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    const keyword = clientKeyword.trim();
    if (keyword.length < 2) {
      setClientOptions([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        setClientLoading(true);
        const response = await MainService.ClientsInAutoComplete(buildClientRequest(keyword));
        setClientOptions(parseList(response).filter((client) => !selectedClients.some((item) => Number(item.Id) === Number(client.Id))));
      } catch (err) {
        console.error('Load clients error:', err);
        setClientOptions([]);
      } finally {
        setClientLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [clientKeyword, selectedClients]);

  const effectivePageSize = pageSize === 'all' ? Math.max(totalCount, 1) : Number(pageSize);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalCount / effectivePageSize) || 1);
  const pageItems = useMemo(() => buildPageItems(page, totalPages), [page, totalPages]);
  const rangeFrom = totalCount === 0 ? 0 : (pageSize === 'all' ? 1 : (page - 1) * Number(pageSize) + 1);
  const rangeTo = totalCount === 0 ? 0 : (pageSize === 'all' ? totalCount : Math.min(page * Number(pageSize), totalCount));
  const colSpan = useMemo(
    () => columns.filter((c) => columnVisibility[c.field] !== false).length + 1,
    [columns, columnVisibility]
  );
  const activeFilterCount = useMemo(
    () => Object.keys(filtersByField).filter((f) => columnHasActiveFilter(filtersByField, f)).length,
    [filtersByField]
  );

  const displayGroups = useMemo(() => {
    if (!groupField) return [{ key: null, label: null, rows }];
    const mapped = rows.map((r) => ({
      ...r,
      [groupField]: getCellValue(r, groupField, parentKey, amountKey),
    }));
    return groupRows(mapped, groupField);
  }, [rows, groupField, parentKey, amountKey]);

  const { isCollapsed, toggleGroup, collapseAll, expandAll } = useGroupCollapse(groupField);

  const groupKeys = useMemo(
    () => displayGroups.filter((g) => g.label != null).map((g) => g.key),
    [displayGroups]
  );

  const updateFilter = (field, value) => setFilter((prev) => ({ ...prev, [field]: value }));
  const addClient = (client) => {
    const nextClients = [...selectedClients, client];
    setSelectedClients(nextClients);
    setFilter((prev) => ({ ...prev, ClientIds: nextClients.map((item) => item.Id) }));
    setClientKeyword('');
    setClientOptions([]);
  };
  const removeClient = (clientId) => {
    const nextClients = selectedClients.filter((client) => Number(client.Id) !== Number(clientId));
    setSelectedClients(nextClients);
    setFilter((prev) => ({ ...prev, ClientIds: nextClients.map((item) => item.Id) }));
  };

  const filterData = () => {
    setAppliedFilter(filter);
    setPage(1);
    fetchRows({ page: 1, applied: filter });
  };

  const handleGroupBy = (field) => {
    if (!groupField) {
      pageSizeBeforeGroupRef.current = { pageSize, page };
    }
    setGroupField(field);
    if (pageSize !== 'all') {
      setPage(1);
      setPageSize('all');
    }
  };

  const handleUngroup = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    if (prev) {
      setPageSize(prev.pageSize);
      setPage(prev.page);
    }
  };

  const refreshData = () => {
    const next = { Date: todayInput(), Keyword: '', EmployeeId: '', Status: '', ClientIds: [] };
    setFilter(next);
    setAppliedFilter(next);
    setSelectedClients([]);
    setClientKeyword('');
    setClientOptions([]);
    setPage(1);
    setSortField('');
    setSortDirection('asc');
    setFiltersByField({});
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    fetchRows({
      page: 1,
      applied: next,
      sortField: '',
      sortDirection: 'asc',
      filters: {},
    });
  };

  const handlePageSizeChange = (value) => {
    const next = value === 'all' ? 'all' : Number(value);
    setPageSize(next);
    setPage(1);
    fetchRows({ page: 1, pageSize: next });
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
    fetchRows({ page: nextPage });
  };

  const handleColumnSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1);
    fetchRows({ page: 1, sortField: field, sortDirection: direction });
  };

  const handleToggleColumn = (field) => {
    setColumnVisibility((prev) => {
      const nextVisible = { ...prev, [field]: prev[field] === false };
      const stillVisible = columns.some((c) => nextVisible[c.field] !== false);
      if (!stillVisible) return prev;
      return nextVisible;
    });
  };

  const handleApplyColumnFilter = (field, colFilter) => {
    const nextFilters = { ...filtersByField, [field]: colFilter };
    setFiltersByField(nextFilters);
    setPage(1);
    fetchRows({ page: 1, filters: nextFilters });
  };

  const handleClearColumnFilter = (field) => {
    const nextFilters = { ...filtersByField };
    delete nextFilters[field];
    setFiltersByField(nextFilters);
    setPage(1);
    fetchRows({ page: 1, filters: nextFilters });
  };

  const clearSortAndFilters = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setFiltersByField({});
    setSortField('');
    setSortDirection('asc');
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    setPage(1);
    if (prev && prev.pageSize !== pageSize) {
      setPageSize(prev.pageSize);
    }
    fetchRows({
      page: 1,
      pageSize: prev?.pageSize ?? pageSize,
      sortField: '',
      sortDirection: 'asc',
      filters: {},
    });
  };

  const exportToExcel = () => {
    try {
      exportGroupedExcel({
        sheetName: title || 'Followup',
        fileName: `client_followup_${new Date().toISOString().split('T')[0]}.xlsx`,
        headers: ['#', ...columns.map((c) => c.title)],
        groups: displayGroups,
        groupFieldTitle: columns.find((c) => c.field === groupField)?.title || 'Group',
        mapRow: (row, serial) => [
          serial,
          ...columns.map((c) => getCellValue(row, c.field, parentKey, amountKey)),
        ],
      });
      setSuccess('Excel file exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      if (err?.message === 'No rows to export') {
        setError('No entries to export');
      } else {
        setError('Failed to export Excel file');
      }
    }
  };

  return (
    <div className="space-y-6">
      {success && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{success}</div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 border-b border-gray-200 pb-4">
          <h1 className="text-2xl font-bold text-gray-900">{title} | {formatDate(appliedFilter.Date)}</h1>
          <p className="mt-1 text-sm text-gray-500">Follow up client {title.toLowerCase().replace(' followup', '')} entries by date, keyword, employee, and status.</p>
        </div>
        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <FilterInput label="Date" type="date" value={filter.Date} onChange={(value) => updateFilter('Date', value)} />
          <FilterSelect label="Employee" value={filter.EmployeeId} onChange={(value) => updateFilter('EmployeeId', value)} options={employees} />
          <FilterInput label="Keyword" value={filter.Keyword} onChange={(value) => updateFilter('Keyword', value)} onEnter={filterData} />
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
            <select value={filter.Status} onChange={(event) => updateFilter('Status', event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20">
              <option value="">All</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ClientMultiSelect
            keyword={clientKeyword}
            setKeyword={setClientKeyword}
            options={clientOptions}
            selectedClients={selectedClients}
            loading={clientLoading}
            onAdd={addClient}
            onRemove={removeClient}
          />
          <div className="flex items-end gap-3">
            <button type="button" onClick={filterData} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50">
              <Search className="h-4 w-4" />
              Filter
            </button>
            <button type="button" onClick={refreshData} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title} List</h2>
            <p className="text-sm text-gray-500">
              {totalCount === 0 ? '0 entries' : `${rangeFrom}–${rangeTo} of ${totalCount}`}
            </p>
          </div>
          <button
            type="button"
            onClick={exportToExcel}
            disabled={rows.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>

        <div className="master-table-scroll relative overflow-x-auto" style={{ maxHeight: 560 }}>
          {loading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2">
                <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-gray-600">Loading...</span>
              </div>
            </div>
          )}

          {(activeFilterCount > 0 || sortField) && (
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-[#f0f9f4] px-4 py-2 text-sm text-gray-700">
              <span>
                {sortField
                  ? `Sorted by ${columns.find((c) => c.field === sortField)?.title || sortField} (${sortDirection})`
                  : 'Filters active'}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} column filter(s)` : ''}
              </span>
              <button type="button" onClick={clearSortAndFilters} className="text-primary hover:underline">
                Clear sort & filters
              </button>
            </div>
          )}

          {groupField && (
            <GroupByBanner
              groupTitle={columns.find((c) => c.field === groupField)?.title || groupField}
              groupKeys={groupKeys}
              onUngroup={handleUngroup}
              onCollapseAll={collapseAll}
              onExpandAll={expandAll}
            />
          )}

          <table className="w-full min-w-[1000px] table-fixed">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                <th className="w-[5%] px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
                {columns.map((column) =>
                  columnVisibility[column.field] === false ? null : (
                    <DataColumnMenu
                      key={column.field}
                      column={column}
                      columns={columns}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleColumnSort}
                      columnVisibility={columnVisibility}
                      onToggleColumn={handleToggleColumn}
                      filtersByField={filtersByField}
                      onApplyFilter={handleApplyColumnFilter}
                      onClearFilter={handleClearColumnFilter}
                      groupField={groupField}
                      onGroupBy={handleGroupBy}
                      onUngroup={handleUngroup}
                    />
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-gray-500">
                    No followup entries found.
                  </td>
                </tr>
              ) : (
                (() => {
                  let serial = 0;
                  return displayGroups.flatMap((group) => {
                    const block = [];
                    if (group.label != null) {
                      block.push(
                        <tr key={`group-${group.key}`} className="bg-gray-100">
                          <td colSpan={colSpan} className="px-4 py-2">
                            <GroupHeaderLabel
                              title={columns.find((c) => c.field === groupField)?.title}
                              label={group.label}
                              count={group.rows.length}
                              collapsed={isCollapsed(group.key)}
                              onToggle={() => toggleGroup(group.key)}
                            />
                          </td>
                        </tr>
                      );
                    }
                    if (group.label != null && isCollapsed(group.key)) {
                      return block;
                    }
                    group.rows.forEach((row, index) => {
                      serial += 1;
                      block.push(
                        <tr key={row.Id || `${group.key}-${index}`} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                            {groupField || pageSize === 'all' ? serial : (page - 1) * Number(pageSize) + serial}
                          </td>
                          {columns.map((column) =>
                            columnVisibility[column.field] === false ? null : (
                              <td
                                key={column.field}
                                className={`whitespace-nowrap px-4 py-2 text-sm ${
                                  column.field === 'clientname' ? 'font-semibold text-gray-900' : 'text-gray-600'
                                }`}
                              >
                                {getCellValue(row, column.field, parentKey, amountKey)}
                              </td>
                            )
                          )}
                        </tr>
                      );
                    });
                    return block;
                  });
                })()
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-3">
              <span>
                {totalCount === 0 ? '0 entries' : `${rangeFrom}–${rangeTo} of ${totalCount}`}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} filter(s)` : ''}
                {groupField
                  ? ` · grouped by ${columns.find((c) => c.field === groupField)?.title || groupField}`
                  : ''}
              </span>
              <label className="flex items-center gap-2">
                <span className="text-gray-500">Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  disabled={loading || !!groupField}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[#46955A] focus:outline-none"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={String(opt)} value={opt}>
                      {opt === 'all' ? 'All' : opt}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {!groupField && pageSize !== 'all' && (
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => goToPage(page - 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Prev
                </button>
                {pageItems.map((item, idx) =>
                  item === '…' ? (
                    <span key={`e-${idx}`} className="px-1.5 text-gray-400">…</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      disabled={loading}
                      onClick={() => goToPage(item)}
                      className={`min-w-[2rem] rounded-lg px-2 py-1.5 text-sm transition-colors ${
                        item === page
                          ? 'bg-[#46955A] font-medium text-white'
                          : 'border border-gray-200 bg-white text-gray-700 hover:bg-[#f0f9f4]'
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
                <button
                  type="button"
                  disabled={page >= totalPages || loading}
                  onClick={() => goToPage(page + 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const FilterInput = ({ label, value, onChange, type = 'text', onEnter }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
    <input type={type} value={value} onChange={(event) => onChange(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && onEnter) onEnter(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
  </div>
);

const FilterSelect = ({ label, value, onChange, options }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
    <select value={value} onChange={(event) => onChange(event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20">
      <option value="">All</option>
      {options.map((option) => <option key={option.Id} value={option.Id}>{option.Name}</option>)}
    </select>
  </div>
);

const ClientMultiSelect = ({ keyword, setKeyword, options, selectedClients, loading, onAdd, onRemove }) => {
  const listOpen = keyword.trim().length >= 2 && !loading && options.length > 0;
  const nav = useListKeyboardNav({
    isOpen: listOpen,
    itemCount: options.length,
    onSelectIndex: (index) => options[index] && onAdd(options[index]),
    onClose: () => setKeyword(''),
    resetKey: keyword,
  });

  return (
    <div className="relative">
      <label className="mb-1 block text-sm font-medium text-gray-700">Clients</label>
      <input
        type="text"
        value={keyword}
        onChange={(event) => setKeyword(event.target.value)}
        onKeyDown={nav.onKeyDown}
        placeholder="Search and select clients..."
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
      {selectedClients.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedClients.map((client) => (
            <span key={client.Id} className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              {client.Name}
              <button type="button" onClick={() => onRemove(client.Id)} className="text-primary hover:text-red-600">x</button>
            </span>
          ))}
        </div>
      )}
      {keyword.trim().length >= 2 && (
        loading ? (
          <div className="absolute z-40 mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-500 shadow-lg">
            Searching clients...
          </div>
        ) : options.length === 0 ? (
          <div className="absolute z-40 mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-500 shadow-lg">
            No clients found
          </div>
        ) : (
          <HighlightList
            items={options}
            highlightedIndex={nav.highlightedIndex}
            setHighlightedIndex={nav.setHighlightedIndex}
            setItemRef={nav.setItemRef}
            onSelect={(client) => onAdd(client)}
            className="absolute z-40 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
            itemClassName="block w-full px-3 py-2 text-left text-sm text-gray-700"
            highlightClassName="bg-gray-50"
            idleClassName="hover:bg-gray-50"
            renderItem={(client) => (
              <>
                <span className="font-medium">{client.Name}</span>
                {client.MobileNo1 && <span className="ml-2 text-xs text-gray-500">{client.MobileNo1}</span>}
              </>
            )}
          />
        )
      )}
    </div>
  );
};

export default ClientFollowupPage;
